import { Chip, IconButton, Tooltip } from "@itk/mui-components";
import { getClickableProps } from "src/utils/keyboard-utils";
import { Box, Stack, Typography, useTheme, Zoom } from "@mui/material";
import { MouseEvent } from "react";
import { useNavigate, useParams } from "react-router";
import { ChipList, DekoxIcon } from "src/components";
import { useConfiguratorPageT } from "src/translation-hooks/configurator-page";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { ROUTES_URL } from "src/utils/constants";
import { connect } from "react-redux";
import { productInformationActions } from "../../../product-information-dialog/product-information-dialog-store";
import { Model_ConfigurationMissingInformationGet, Model_ConfigurationsGet_ProjectLineItem } from "src/backend/openapi/v1";
import { applicationActions } from "src/components/application-store";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { TConfigureProductSearchParams } from "src/pages/configure-product/configure-product-types";
import { getLabelsForEntity } from "src/pages/configurator/configurator-utils";
import { RootState } from "src/store";

const mapStateToProps = (state: RootState) => ({
  labels: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.labels
});

const ownActions = {
  setIsProductInformationDialogOpen: productInformationActions.setIsProductInformationDialogOpen,
  deleteProjectLineItemsRequest: configuratorActions.deleteProjectLineItemsRequest,
  getConfigurationsRequest: configuratorActions.getConfigurationsRequest,
  setWarningAction: applicationActions.setWarningAction
};

type Props = typeof ownActions &
  ReturnType<typeof mapStateToProps> & {
    missingInformation?: Model_ConfigurationMissingInformationGet[];
    projectLineItem: Model_ConfigurationsGet_ProjectLineItem;
    isProductTab?: boolean;
    configurationName?: string;
  };

const ProductConfigurationBase = (props: Props) => {
  const {
    setIsProductInformationDialogOpen,
    projectLineItem,
    setWarningAction,
    deleteProjectLineItemsRequest,
    getConfigurationsRequest,
    isProductTab = false,
    configurationName,
    labels,
    missingInformation
  } = props;
  const theme = useTheme();
  const navigate = useNavigate();

  const configuratorPageT = useConfiguratorPageT();
  const genericTranslationsT = useGenericTranslationsT();

  const { projectId = "" } = useParams<{ projectId: string }>();

  const missingInformationsForProjectLineItem = missingInformation?.filter(missingInfo => missingInfo.projectLineItemId === projectLineItem.projectLineItemId) || [];

  const onEdit = (e: MouseEvent) => {
    e.stopPropagation();
    if (projectLineItem.configurationId && projectLineItem.projectLineItemId) {
      navigate({
        pathname: ROUTES_URL.CONFIGURE_PRODUCT.build(projectId, projectLineItem.configurationId),
        search: `?${new URLSearchParams({ projectLineItemId: projectLineItem.projectLineItemId } as TConfigureProductSearchParams).toString()}`
      });
    }
  };

  const onDelete = (e: MouseEvent) => {
    e.stopPropagation();
    setWarningAction({
      confirm: () => {
        if (!projectLineItem.projectLineItemId) return;

        deleteProjectLineItemsRequest({ projectLineItemIds: [projectLineItem.projectLineItemId] }, isSuccess => {
          if (isSuccess) {
            getConfigurationsRequest(projectId);
          }
        });
        setWarningAction(undefined);
      },
      dialogProps: {
        message: configuratorPageT.areYouSureYouWantToDeleteThisProductConfiguration,
        confirmButtonTranslation: {
          t: genericTranslationsT,
          k: genericTranslationsK,
          field: "delete"
        },
        messageIcon: <DekoxIcon icon="delete" />
      },
      variant: "confirmCancel"
    });
  };

  const handleOpenProduct = () => {
    if (!projectLineItem.productId) return;
    setIsProductInformationDialogOpen({ productId: projectLineItem.productId });
  };

  return (
    <>
      <Stack
        direction="row"
        {...getClickableProps(handleOpenProduct)}
        sx={{
          borderRadius: 1,
          border: `1px solid ${theme.palette.customColors.grey}`,
          backgroundColor: !isProductTab ? theme.palette.customColors.lightGrey2 : theme.palette.customColors.white,
          cursor: "pointer",
          outline: "none",
          "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` }
        }}
        onClick={handleOpenProduct}
      >
        <Stack gap={2} direction="row" alignItems="center" width="100%" overflow="hidden" px={1.5} py={1}>
          {/* Product name */}
          <Stack flex="none" width={250}>
            <Typography variant="subtitle2" fontWeight="500" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
              {projectLineItem.productDescription} ({projectLineItem.productNumber})
            </Typography>
          </Stack>

          {/* Chips */}
          <Stack flex={1} direction="row" alignItems="center" gap={1} overflow="hidden">
            <Chip label={projectLineItem.productTypeName} size="small" sx={{ borderRadius: 1, bgcolor: theme.palette.customColors.white, border: `1px solid ${theme.palette.customColors.grey2}` }} />
            {projectLineItem?.projectLineItemId && <ChipList labels={getLabelsForEntity(labels, projectLineItem.projectLineItemId).map(label => label.shortname)} />}
          </Stack>

          {/* Action buttons */}
          <Stack flex="none" direction="row" alignItems="center" justifyContent="flex-end">
            {configurationName && (
              <Typography variant="subtitle1" mr={2}>
                {configurationName}
              </Typography>
            )}

            <Tooltip
              title={
                <Box>
                  <Box>{configuratorPageT.productIsMissingInformation}</Box>
                  <Box component="ul" sx={{ m: 0, pl: 2 }}>
                    {missingInformationsForProjectLineItem.map((info, index) => (
                      <li key={index}>{info.attributeDescription}</li>
                    ))}
                  </Box>
                </Box>
              }
              arrow
              placement="bottom"
              slots={{ transition: Zoom }}
            >
              <DekoxIcon icon="info" fontSize="small" color="warning" sx={{ mx: 1, visibility: missingInformationsForProjectLineItem.length > 0 ? "visible" : "hidden" }} />
            </Tooltip>
            <IconButton k={genericTranslationsK.edit} size="small" onClick={onEdit} sx={{ color: theme.palette.customColors.darkGrey }}>
              <DekoxIcon icon="edit" fontSize="small" />
            </IconButton>
            <IconButton k={genericTranslationsK.delete} size="small" onClick={onDelete} sx={{ color: theme.palette.customColors.darkGrey }}>
              <DekoxIcon icon="delete" fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};

export const ProductConfiguration = connect(mapStateToProps, ownActions)(ProductConfigurationBase);
